import {
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AppContext } from 'context/appContext';
import React from 'react';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ActionFunction,
  Outlet,
  useFetcher,
  useNavigate,
  useOutletContext,
  useLocation,
} from 'react-router';
import MobilePanelList from 'components/lists/MobilePanelList';
import { ROUTES, routeWithParams } from 'router';
import {
  EntityTypes,
  FormMode,
  PanelRootContext,
  PropertyGroup,
  PropertyGroupListItem,
  TraxeroActionResponse,
  Status,
} from 'types';
import {
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_SortingState,
  MRT_RowVirtualizer,
  MaterialReactTable,
} from 'material-react-table';
import { History, Add, Warning } from '@mui/icons-material';
import { useDefaultTableOptions } from 'hooks/useDefaultTableOptions';
import { useTranslation } from 'react-i18next';
import { getBlankPropertyGroup, logError } from 'utils';
import { Tenant } from 'types';
import { useFetcherKey } from 'hooks/useFetcherKey';
import { ToastContext, TOAST_TYPE } from 'context/toastContext';
import {
  PropertyGroupRouteParams,
  PropertyGroupsActionHandler,
} from './PropertyGroupLayout';
import { createPropertyGroup, patchPropertyGroup } from 'api/propertyGroup';
import AuthService from 'utils/AuthService';
import MRTableToolbar from 'components/tables/PanelTableToolbar';
import FormHeaderBar from 'components/layout/FormHeaderBar';

export interface PropertyGroupPanelContext {
  propertyGroup: PropertyGroup;
  setPropertyGroup: (propertyGroup: PropertyGroup) => void;
  formMode: FormMode;
  fetcherKey: string;
  onBack: () => void;
}

const PropertyGroupsPanel = (): ReactNode => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const displayToast = useContext(ToastContext);
  const theme = useTheme();
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const { formMode, setFormMode, propertyGroups } =
    useOutletContext<PanelRootContext<Tenant>>();
  const { state } = useContext(AppContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [newGroup, setNewGroup] = useState<PropertyGroup>(
    getBlankPropertyGroup(state.tenant.id),
  );
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  useEffect(() => {
    //scroll to the top of the table when the sorting changes
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      logError(error as Error);
    }
  }, [sorting]);

  useEffect(() => {
    if (fetcher.data) {
      const response =
        fetcher.data as TraxeroActionResponse<PropertyGroupActionHandler>;
      let message = '';
      if (response.status < 400) {
        switch (response.handler) {
          case PropertyGroupActionHandler.DELETE:
            message = t('delete_success', {
              name: `${t('property_group.label', { ns: 'entities' })} ${response.body?.name || ''}`,
              ns: 'toasts',
            });
            break;
          case PropertyGroupActionHandler.CREATE:
            message = t('create_success', {
              name: `${newGroup?.name}`,
              ns: 'toasts',
            });
            break;
          case PropertyGroupActionHandler.UPDATE:
            message = t('update_success', {
              name: `${t('property_group.label', { ns: 'entities' })}`,
              ns: 'toasts',
            });
            break;
        }
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message,
        });
      } else {
        switch (response.handler) {
          case PropertyGroupActionHandler.DELETE:
            message = t('delete_error', {
              name: `${t('property_group.label', { ns: 'entities' })}`,
              error: `${response.status} - ${response.statusText}`,
              ns: 'toasts',
            });
            break;
          case PropertyGroupActionHandler.CREATE:
            message = t('create_error', {
              name: `${newGroup?.name}`,
              error: `${response.status} - ${response.statusText}`,
              ns: 'toasts',
            });
            break;
          case PropertyGroupActionHandler.UPDATE:
            message = t('update_error', {
              name: `${t('property_group.label', { ns: 'entities' })}`,
              error: `${response.status} - ${response.statusText}`,
              ns: 'toasts',
            });
            break;
        }
        displayToast({
          type: TOAST_TYPE.ERROR,
          message,
        });
      }
    }
  }, [fetcher.data]);

  const handleItemClick = useCallback(
    (group: PropertyGroupListItem) => {
      navigate(
        routeWithParams(ROUTES.PROPERTY_GROUP, [state.tenant.id, group.id]),
      );
    },
    [state.tenant.id],
  );
  const handleCreateClick = () => {
    navigate(routeWithParams(ROUTES.PROPERTY_GROUP_CREATE, [state.tenant.id]));
  };
  const handleViewRevisions = (pg: PropertyGroupListItem) => {
    navigate(
      routeWithParams(ROUTES.REVISIONS, [
        state.tenant.id,
        EntityTypes.PropertyGroup,
        pg.id,
      ]),
    );
  };
  const handleSoftDelete = (propertyGroup: PropertyGroupListItem): void => {
    const body = {
      id: propertyGroup.id,
      name: propertyGroup.name,
      description: propertyGroup.description,
      status: Status.Disabled,
      tenantID: propertyGroup.tenantID,
    };
    fetcher.submit(
      {
        handler: PropertyGroupActionHandler.DELETE,
        body: JSON.stringify(body),
        tenantID: state.tenant.id,
        groupID: propertyGroup.id,
      },
      {
        method: 'PUT',
        action: pathname,
        encType: 'application/json',
      },
    );
  };

  const columns: MRT_ColumnDef<PropertyGroupListItem>[] = [
    {
      header: t('property_group.id', { ns: 'entities' }),
      accessorKey: 'id',
    },
    {
      header: t('property_group.name', { ns: 'entities' }),
      accessorKey: 'name',
    },
    {
      header: t('property_group.description', { ns: 'entities' }),
      accessorKey: 'description',
    },
    {
      header: t('property.label_plural', { ns: 'entities' }),
      accessorKey: 'propertyCount',
    },
  ];
  const tableDefaults = useDefaultTableOptions<PropertyGroupListItem>();
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: (propertyGroups as PropertyGroupListItem[]) || [],
    columns,
    enableRowVirtualization: true,
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 5 },
    enableRowSelection: false,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      columnVisibility: {
        id: true,
        name: true,
        description: true,
        propertyCount: true,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
      density: 'comfortable',
    },
    state: {
      sorting,
      showLoadingOverlay: false,
    },
    onSortingChange: setSorting,
    getRowId: (row) => `${row.id}`,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: 'white',
        cursor: 'pointer',
      },
      onClick: () => handleItemClick(row.original),
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        hidden={formMode !== 'none'}
        isSubmitting={fetcher.state === 'submitting'}
        optionsDisabled={!state.entityPermissions.property_group.create}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', {
              name: t('property_group.label', { ns: 'entities' }),
            }),
            disabled: !state.entityPermissions.property_group.create,
            icon: <Add />,
          },
        ]}
      />
    ),

    renderRowActionMenuItems: ({ closeMenu, row }) => {
      const options = [
        <MenuItem
          key={0}
          divider
          onClick={() => {
            handleViewRevisions(row.original);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
        >
          <ListItemIcon>
            <History />
          </ListItemIcon>
          {t('buttons.view', { name: t('revision.label_plural') })}
        </MenuItem>,
      ];
      if (AuthService.IsSuperAdmin()) {
        options.push(
          <MenuItem
            key={1}
            onClick={() => {
              handleSoftDelete(row.original);
              closeMenu();
            }}
            sx={{
              m: 0,
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
            }}
          >
            <ListItemIcon>
              <Warning sx={{ color: 'white' }} />
            </ListItemIcon>
            {t('buttons.delete', {
              name: t('property_group.label', { ns: 'entities' }),
            })}
          </MenuItem>,
        );
      }
      return options;
    },
  });

  const handleCancelAction = () => {
    setNewGroup(getBlankPropertyGroup(state.tenant.id));
    setFormMode('none');
  };

  if (formMode !== 'none') {
    return (
      <React.Fragment>
        <FormHeaderBar />
        <Outlet
          context={
            {
              propertyGroup: newGroup,
              setPropertyGroup: setNewGroup,
              formMode,
              onBack: handleCancelAction,
              fetcherKey,
            } satisfies PropertyGroupPanelContext
          }
        />
      </React.Fragment>
    );
  }

  if (isMobile) {
    const itemHeight = 70;
    const renderItem = (propertyGroup: PropertyGroupListItem): ReactNode => (
      <ListItem
        key={propertyGroup.id}
        onClick={() => handleItemClick(propertyGroup)}
        divider
        sx={{
          height: itemHeight,
          backgroundColor: 'white',
        }}
      >
        <ListItemText
          primary={propertyGroup.name}
          secondary={propertyGroup.description}
          primaryTypographyProps={{
            variant: 'subtitle1',
            noWrap: true,
          }}
          secondaryTypographyProps={{
            variant: 'subtitle2',
            noWrap: true,
          }}
        />
        <ListItemAvatar>
          <Chip
            sx={{ px: 1, fontWeight: 'bold' }}
            label={`${propertyGroup.propertyCount} ${t(
              'property.label_plural',{ ns: 'entities' }
            )}`}
            size={'small'}
            color={'info'}
          />
        </ListItemAvatar>
      </ListItem>
    );

    return (
      <MobilePanelList<PropertyGroupListItem>
        data={(propertyGroups as PropertyGroupListItem[]) || []}
        itemHeight={itemHeight}
        columns={columns.map((c) => ({
          name: t(c.header),
          searchKey: c.accessorKey,
        }))}
        renderItem={renderItem}
        title={t('property_group.label_plural', { ns: 'entities' })}
        withSearchBar
        menuOptions={[
          {
            content: t('buttons.create', { name: t('property_group.label', { ns: 'entities' }) }),
            onClick: handleCreateClick,
            disabled: !state.entityPermissions.property_group.create,
            icon: <Add />,
          },
        ]}
      />
    );
  }

  return <MaterialReactTable table={table} />;
};

export enum PropertyGroupActionHandler {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  UNKNOWN = 'unknown',
}

export const propertyGroupAction: ActionFunction = async ({
  params,
  request,
}): Promise<TraxeroActionResponse<PropertyGroupActionHandler>> => {
  const { body, handler, groupID } = await request.json();
  const { tenantID } = params as PropertyGroupRouteParams['params'];
  let res: Response;

  try {
    switch (handler) {
      case PropertyGroupsActionHandler.CREATE:
        res = await createPropertyGroup({ body, tenantID });
        break;
      case PropertyGroupsActionHandler.DELETE:
      case PropertyGroupsActionHandler.UPDATE:
        res = await patchPropertyGroup({ body, tenantID, groupID });
        break;
      default:
        res = new Response('Unknown handler', {
          status: 404,
          statusText: 'Unknown Handler',
        });
    }
  } catch (error) {
    res = new Response('', { status: 500, statusText: 'Network Error' });
  }
  return {
    handler,
    status: res.status,
    statusText: res.statusText,
  };
};

export default PropertyGroupsPanel;
